// CallDetailModal.js
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useSharedData } from '../contexts/SharedDataContext';

// Custom spinner icon for loading state
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

// This component uses a portal to render the modal outside of the normal React tree
const CallDetailModal = React.memo(({ callId, visible, onClose }) => {
    const [callHtml, setCallHtml] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { getAccessTokenSilently } = useAuth0();
    const { selectedTenant } = useSharedData();

    // Keep track of modal element reference
    const [modalRoot, setModalRoot] = useState(null);

    // Find or create the modal root element on mount
    useEffect(() => {
        let element = document.getElementById('modal-root');
        if (!element) {
            element = document.createElement('div');
            element.id = 'modal-root';
            document.body.appendChild(element);
        }
        setModalRoot(element);

        // Cleanup on unmount
        return () => {
            // Don't remove the element if it might be used by other modals
            // If this is the only modal using it, you could remove it here
        };
    }, []);

    // Load call HTML only when modal becomes visible with a new callId
    useEffect(() => {
        if (visible && callId) {
            let isMounted = true; // For preventing updates after unmount

            const fetchCallHtml = async () => {
                setError(null);
                setLoading(true);
                try {
                    const token = await getAccessTokenSilently();
                    const response = await fetch(`/api/calls/${callId}/html`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'x-tenant': selectedTenant,
                        },
                    });

                    if (!response.ok) {
                        throw new Error('Failed to fetch call HTML');
                    }

                    const html = await response.text();

                    // Only update state if the component is still mounted
                    if (isMounted) {
                        setCallHtml(html);
                        setLoading(false);
                    }
                } catch (err) {
                    console.error('Error fetching call HTML:', err);
                    // Only update state if the component is still mounted
                    if (isMounted) {
                        setError('Failed to fetch call details');
                        setLoading(false);
                    }
                }
            };

            fetchCallHtml();

            // Cleanup function to prevent state updates after unmount
            return () => {
                isMounted = false;
            };
        }
    }, [visible, callId, getAccessTokenSilently, selectedTenant]);

    // Don't render anything if modal root is not ready or modal is not visible
    if (!modalRoot || !visible) return null;

    // Content to render inside the modal
    const modalContent = (
        <Modal
            open={visible}
            onCancel={onClose}
            width={1000}
            footer={null}
            style={{ top: 20 }}
            bodyStyle={{ padding: 0, height: '80vh' }}
            destroyOnClose={true}
            maskClosable={true}
        >
            {loading ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%'
                }}>
                    <Spin indicator={antIcon} />
                    <span style={{ marginLeft: 12 }}>Loading call details...</span>
                </div>
            ) : error ? (
                <div style={{
                    padding: 20,
                    color: 'red',
                    display: 'flex',
                    justifyContent: 'center'
                }}>
                    {error}
                </div>
            ) : (
                <iframe
                    title="FullCallHtml"
                    srcDoc={callHtml}
                    style={{
                        border: 'none',
                        width: '100%',
                        height: '100%',
                    }}
                />
            )}
        </Modal>
    );

    // Use ReactDOM.createPortal to render the modal outside the normal component tree
    return ReactDOM.createPortal(modalContent, modalRoot);
});

export default CallDetailModal;
